import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Check, Copy, Loader2, Mail, PauseCircle, PlayCircle, Search, SkipForward, Square } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, Pill, buttonClass, inputClass } from "../components/Bits";

const CHANNEL_TONE = { email: "gold", call: "terra", linkedin: "sage" };

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Your browser blocked the clipboard — select the text and copy it");
  }
};

const mailtoHref = (step) =>
  `mailto:${step.to_email || ""}?subject=${encodeURIComponent(step.subject)}` +
  `&body=${encodeURIComponent(step.body)}`;

const StepCard = ({ step, onSent, onSkip, onMeeting, onFindEmail }) => (
  <li className="space-y-3 py-5" data-testid={`queue-step-${step.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <Link to={`/admin/accounts/${step.account_id}`}
              className="text-sm text-cream transition-colors duration-200 hover:text-gold"
              data-testid={`queue-account-${step.id}`}>
          {step.account_name}
        </Link>
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
          {step.sequence_name} · step {step.step_index + 1} of {step.step_total} · due {step.due_date}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Pill tone={CHANNEL_TONE[step.channel]}>{step.channel}</Pill>
        {step.to_email ? null : (
          <Pill tone="neutral">{step.channel === "email" ? "no contact email" : "no contact"}</Pill>
        )}
      </div>
    </div>

    <div className="rounded-lg border border-cream/10 bg-cream/5 p-4">
      <p className="text-sm text-cream/90" data-testid={`queue-subject-${step.id}`}>{step.subject}</p>
      <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-cream/55">{step.body}</p>
      {step.to_email || step.to_phone ? (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
          {[step.to_name, step.to_email, step.to_phone].filter(Boolean).join(" · ")}
        </p>
      ) : null}
    </div>

    <div className="flex flex-wrap items-center gap-3">
      {step.channel === "email" ? (
        <a href={mailtoHref(step)} className={buttonClass} data-testid={`queue-open-${step.id}`}>
          <Mail className="h-3.5 w-3.5" /> Open in email
        </a>
      ) : null}
      {step.channel === "email" && !step.to_email ? (
        <button type="button" onClick={() => onFindEmail(step)}
                data-testid={`queue-find-email-${step.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs text-gold transition-colors duration-200 hover:bg-gold/10">
          <Search className="h-3.5 w-3.5" /> Find the email
        </button>
      ) : null}
      <button type="button" onClick={() => copyText(`${step.subject}\n\n${step.body}`, "Draft")}
              data-testid={`queue-copy-${step.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-4 py-2 text-xs text-cream/70 transition-colors duration-200 hover:border-gold/50 hover:text-cream">
        <Copy className="h-3.5 w-3.5" /> Copy
      </button>
      <button type="button" onClick={() => onSent(step)} data-testid={`queue-sent-${step.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-sage/40 px-4 py-2 text-xs text-sage transition-colors duration-200 hover:bg-sage/10">
        <Check className="h-3.5 w-3.5" /> {step.channel === "call" ? "Mark called" : "Mark sent"}
      </button>
      <button type="button" onClick={() => onMeeting(step)} data-testid={`queue-meeting-${step.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-xs text-gold transition-colors duration-200 hover:bg-gold/20">
        <CalendarCheck className="h-3.5 w-3.5" /> Booked a meeting
      </button>
      <button type="button" onClick={() => onSkip(step)} data-testid={`queue-skip-${step.id}`}
              className="inline-flex items-center gap-2 text-xs text-cream/40 transition-colors duration-200 hover:text-terra">
        <SkipForward className="h-3.5 w-3.5" /> Skip
      </button>
    </div>
  </li>
);

const Builder = ({ onSaved }) => {
  const [form, setForm] = useState({ name: "", description: "" });
  const [steps, setSteps] = useState([
    { day: 0, channel: "email", subject: "", body: "" },
  ]);
  const [busy, setBusy] = useState(false);

  const setStep = (index, key, value) =>
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/crm/sequences", {
        name: form.name,
        description: form.description || null,
        steps: steps.map((s) => ({ ...s, day: Number(s.day) })),
      });
      toast.success("Cadence saved");
      setForm({ name: "", description: "" });
      setSteps([{ day: 0, channel: "email", subject: "", body: "" }]);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="sequence-builder-form">
      <input required value={form.name} className={inputClass} placeholder="Cadence name"
             data-testid="sequence-name-input"
             onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input value={form.description} className={inputClass} placeholder="What this cadence is for"
             data-testid="sequence-description-input"
             onChange={(e) => setForm({ ...form, description: e.target.value })} />

      {steps.map((step, index) => (
        <div key={index} className="space-y-2 rounded-lg border border-cream/10 p-3"
             data-testid={`sequence-step-${index}`}>
          <div className="flex gap-2">
            <input type="number" min="0" value={step.day} className={`${inputClass} w-24`}
                   data-testid={`sequence-step-day-${index}`}
                   onChange={(e) => setStep(index, "day", e.target.value)} />
            <select value={step.channel} className={inputClass}
                    data-testid={`sequence-step-channel-${index}`}
                    onChange={(e) => setStep(index, "channel", e.target.value)}>
              <option value="email">Email</option>
              <option value="call">Call</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>
          <input required value={step.subject} className={inputClass} placeholder="Subject line"
                 data-testid={`sequence-step-subject-${index}`}
                 onChange={(e) => setStep(index, "subject", e.target.value)} />
          <textarea required value={step.body} rows={4} className={inputClass}
                    placeholder="Body — use {{company}}, {{first_name}}, {{renewal_month}}"
                    data-testid={`sequence-step-body-${index}`}
                    onChange={(e) => setStep(index, "body", e.target.value)} />
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" data-testid="sequence-add-step"
                onClick={() => setSteps((prev) => [...prev, {
                  day: (Number(prev[prev.length - 1]?.day) || 0) + 3,
                  channel: "email", subject: "", body: "",
                }])}
                className="rounded-full border border-cream/15 px-4 py-2 text-xs text-cream/70 transition-colors duration-200 hover:border-gold/50 hover:text-cream">
          Add a step
        </button>
        <button type="submit" disabled={busy} className={buttonClass} data-testid="sequence-save">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save cadence
        </button>
      </div>
      <p className="text-xs leading-relaxed text-cream/35">
        Day is counted from the previous step. Tokens:{" "}
        {"{{company}} {{first_name}} {{city}} {{carrier}} {{broker}} {{peo}} {{renewal_month}} {{lives}} {{sender}} {{peo_pick}} {{peo_pick_why}} {{peo_backup}}"}
      </p>
      <p className="text-xs leading-relaxed text-cream/30">
        {"{{peo_pick}}"} fills in the partner the best-fit matrix picks for that employer, with{" "}
        {"{{peo_pick_why}}"} as the one-line reason and {"{{peo_backup}}"} as the quote-alongside.
      </p>
    </form>
  );
};

const Scoreboard = ({ data }) => {
  if (!data || data.cadences.length === 0) {
    return (
      <p className="text-sm leading-relaxed text-cream/40" data-testid="scoreboard-empty">
        Nothing to score yet. Once touches go out and accounts start moving, each step's share of
        the meetings shows up here.
      </p>
    );
  }
  return (
    <div className="space-y-6">
      <p className="text-xs leading-relaxed text-cream/35" data-testid="scoreboard-basis">
        {data.basis}
      </p>
      {data.cadences.map((cadence) => (
        <div key={cadence.sequence_id} data-testid={`scoreboard-${cadence.sequence_id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="text-sm text-cream/90">{cadence.sequence_name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
              {cadence.sent} sent · {cadence.meetings} meetings ·{" "}
              {Math.round(cadence.meeting_rate * 100)}%
            </p>
          </div>
          <table className="mt-3 w-full border-collapse text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                <th className="py-2 pr-3">Step</th>
                <th className="py-2 pr-3">Sent</th>
                <th className="py-2 pr-3">Skipped</th>
                <th className="py-2 pr-3">Meetings</th>
                <th className="py-2">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream/10">
              {cadence.steps.map((step) => (
                <tr key={step.step_index} data-testid={`scoreboard-step-${cadence.sequence_id}-${step.step_index}`}>
                  <td className="py-2.5 pr-3 text-xs text-cream/80">
                    {step.step_index + 1}. {step.channel}
                    <span className="block text-[10px] text-cream/30">{step.subject}</span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-cream/60">{step.sent}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-cream/40">{step.skipped}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-gold">{step.meetings}</td>
                  <td className="py-2.5 font-mono text-xs text-cream/60">
                    {Math.round(step.reply_rate * 100)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const money = (value) => (value ? `$${Math.round(value).toLocaleString()}` : "—");
const pct = (value) => `${Math.round((value || 0) * 100)}%`;

const WinRate = ({ data }) => {
  if (!data || data.cadences.length === 0) {
    return (
      <p className="text-sm leading-relaxed text-cream/40" data-testid="winrate-empty">
        No signed business attributed yet. The moment a deal lands in Closed Won — or an account is
        marked won — the touch that preceded it shows up here with the revenue beside it.
      </p>
    );
  }
  return (
    <div className="space-y-6" data-testid="winrate-body">
      <div className="grid gap-4 sm:grid-cols-4">
        {[["Accounts worked", data.totals.accounts_touched, "winrate-touched-stat"],
          ["Meetings", data.totals.meetings, "winrate-meetings-stat"],
          ["Signed", data.totals.wins, "winrate-wins-stat"],
          ["Signed value", money(data.totals.revenue), "winrate-revenue-stat"]]
          .map(([label, value, testid]) => (
          <div key={label} className="rounded-lg border border-cream/10 bg-cream/5 px-4 py-4"
               data-testid={testid}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">{label}</p>
            <p className="mt-2 font-serif text-2xl leading-none text-cream">{value}</p>
          </div>
        ))}
      </div>

      {data.cadences.map((cadence) => (
        <div key={cadence.sequence_id} data-testid={`winrate-${cadence.sequence_id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="text-sm text-cream/90">{cadence.sequence_name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
              {cadence.accounts_touched} worked · {cadence.meetings} meetings ·{" "}
              <span className="text-gold">{cadence.wins} signed ({pct(cadence.win_rate)})</span>
              {cadence.revenue ? ` · ${money(cadence.revenue)}` : ""}
            </p>
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/25">
            {pct(cadence.meeting_to_win)} of meetings signed
            {cadence.avg_days_to_win !== null
              ? ` · ${cadence.avg_days_to_win} days from last touch to signature` : ""}
            {cadence.open_value ? ` · ${money(cadence.open_value)} still open` : ""}
          </p>
          <table className="mt-3 w-full border-collapse text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                <th className="py-2 pr-3">Channel</th>
                <th className="py-2 pr-3">Touches</th>
                <th className="py-2 pr-3">Signed</th>
                <th className="py-2 pr-3">Value</th>
                <th className="py-2">Per touch</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream/10">
              {cadence.by_channel.map((row) => (
                <tr key={row.channel}
                    data-testid={`winrate-channel-${cadence.sequence_id}-${row.channel}`}>
                  <td className="py-2.5 pr-3 text-xs text-cream/80">{row.channel}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-cream/60">{row.sent}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-gold">{row.wins}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-sage">{money(row.revenue)}</td>
                  <td className="py-2.5 font-mono text-xs text-cream/70">
                    {row.value_per_touch ? `$${row.value_per_touch.toLocaleString()}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="mt-5 w-full border-collapse text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                <th className="py-2 pr-3">Step</th>
                <th className="py-2 pr-3">Sent</th>
                <th className="py-2 pr-3">Signed</th>
                <th className="py-2 pr-3">Influenced</th>
                <th className="py-2 pr-3">Value</th>
                <th className="py-2">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream/10">
              {cadence.steps.map((step) => (
                <tr key={step.step_index}
                    data-testid={`winrate-step-${cadence.sequence_id}-${step.step_index}`}>
                  <td className="py-2.5 pr-3 text-xs text-cream/80">
                    {step.step_index + 1}. {step.channel}
                    <span className="block text-[10px] text-cream/30">{step.subject}</span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-cream/60">{step.sent}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-gold">{step.wins}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-cream/40">{step.influenced}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-sage">{money(step.revenue)}</td>
                  <td className="py-2.5 font-mono text-xs text-cream/60">{pct(step.win_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <p className="text-xs leading-relaxed text-cream/35" data-testid="winrate-basis">
        {data.basis}
      </p>
    </div>
  );
};

const LostReasons = ({ data }) => {
  if (!data || data.cadences.length === 0) {
    return (
      <div className="space-y-4" data-testid="lostreasons-empty">
        <p className="text-sm leading-relaxed text-cream/40">
          Nothing closed out as lost yet. As deals die, the reason lands here against the cadence that
          opened them — that's how you spot an opener pulling the wrong fit.
        </p>
        <div className="flex flex-wrap gap-2">
          {(data?.standard_reasons || []).map((reason) => (
            <span key={reason} data-testid={`lostreason-option-${reason}`}
                  className="rounded-full border border-cream/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
              {reason}
            </span>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="lostreasons-body">
      <div className="flex flex-wrap gap-2" data-testid="lostreasons-totals">
        {data.totals.map((row) => (
          <span key={row.reason} data-testid={`lostreason-total-${row.reason}`}
                className="rounded-full border border-cream/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/55">
            {row.reason} · {row.count}
            {row.value ? ` · ${money(row.value)}` : ""}
          </span>
        ))}
      </div>

      {data.cadences.map((cadence) => (
        <div key={cadence.sequence_id || "none"}
             data-testid={`lostreasons-${cadence.sequence_id || "none"}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="text-sm text-cream/90">{cadence.sequence_name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
              {cadence.lost} lost
              {cadence.value ? ` · ${money(cadence.value)} gone` : ""}
              {cadence.top_reason ? ` · mostly ${cadence.top_reason.toLowerCase()}` : ""}
            </p>
          </div>
          <ul className="mt-2 divide-y divide-cream/10">
            {cadence.reasons.map((row) => (
              <li key={row.reason} className="flex items-baseline justify-between gap-3 py-2"
                  data-testid={`lostreason-${cadence.sequence_id || "none"}-${row.reason}`}>
                <span className="text-xs text-cream/75">
                  {row.reason}
                  {row.channel ? (
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30">
                      last touch {row.channel}
                    </span>
                  ) : null}
                </span>
                <span className="font-mono text-xs text-cream/55">
                  {row.count}{row.value ? ` · ${money(row.value)}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="text-xs leading-relaxed text-cream/35" data-testid="lostreasons-basis">
        {data.basis}
      </p>
    </div>
  );
};

const ChannelBudget = ({ data, capacity, onCapacity }) => {
  if (!data) return <p className="text-sm text-cream/40">Working out the week…</p>;
  return (
    <div className="space-y-5" data-testid="channel-plan-body">
      <div className="flex flex-wrap items-center gap-3">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40"
               htmlFor="channel-capacity">
          Touches you can run a week
        </label>
        <input id="channel-capacity" type="number" min="10" max="2000" step="10" value={capacity}
               data-testid="channel-capacity-input"
               onChange={(e) => onCapacity(Number(e.target.value) || 10)}
               className="w-24 rounded border border-cream/15 bg-cream/5 px-3 py-1.5 text-center font-mono text-xs text-cream outline-none focus:border-gold/60" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/70"
              data-testid="channel-hours">
          ≈ {data.hours_per_week} hours
        </span>
      </div>

      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
            <th className="py-2 pr-3">Channel</th>
            <th className="py-2 pr-3">A week</th>
            <th className="py-2 pr-3">Share</th>
            <th className="py-2 pr-3">Time</th>
            <th className="py-2">Evidence so far</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-cream/10">
          {data.channels.map((row) => (
            <tr key={row.channel} data-testid={`channel-plan-${row.channel}`}>
              <td className="py-2.5 pr-3 text-xs text-cream/80">{row.channel}</td>
              <td className="py-2.5 pr-3 font-serif text-xl leading-none text-gold">
                {row.touches_per_week}
              </td>
              <td className="py-2.5 pr-3 font-mono text-xs text-cream/55">
                {Math.round(row.share * 100)}%
              </td>
              <td className="py-2.5 pr-3 font-mono text-xs text-cream/45">
                {Math.round(row.minutes_per_week / 6) / 10}h
              </td>
              <td className="py-2.5 text-xs leading-relaxed text-cream/40">{row.why}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-xs leading-relaxed text-cream/35" data-testid="channel-plan-source">
        Weighted on {data.source}. {data.basis}
      </p>
    </div>
  );
};

export default function AdminSequences() {
  const [sequences, setSequences] = useState([]);
  const [lists, setLists] = useState([]);
  const [queue, setQueue] = useState({ steps: [], due_today: 0, upcoming: 0, sent_total: 0 });
  const [enrollments, setEnrollments] = useState([]);
  const [enrollForm, setEnrollForm] = useState({ sequence_id: "", list_id: "", limit: 25 });
  const [nudges, setNudges] = useState([]);
  const [scores, setScores] = useState(null);
  const [winRate, setWinRate] = useState(null);
  const [lost, setLost] = useState(null);
  const [plan, setPlan] = useState(null);
  const [capacity, setCapacity] = useState(100);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [seq, list, q, enr, nud, score, won, dead] = await Promise.allSettled([
      adminApi.get("/crm/sequences"),
      adminApi.get("/crm/lists"),
      adminApi.get("/crm/sequences/queue", { params: { when: "due", limit: 50 } }),
      adminApi.get("/crm/sequences/enrollments", { params: { status: "all", limit: 100 } }),
      adminApi.get("/crm/sequences/nudges"),
      adminApi.get("/crm/sequences/scoreboard"),
      adminApi.get("/crm/sequences/win-rate"),
      adminApi.get("/crm/sequences/lost-reasons"),
    ]);
    if (seq.status === "fulfilled") setSequences(seq.value.data.sequences);
    if (list.status === "fulfilled") setLists(list.value.data.lists);
    if (q.status === "fulfilled") setQueue(q.value.data);
    if (nud.status === "fulfilled") setNudges(nud.value.data.nudges);
    if (score.status === "fulfilled") setScores(score.value.data);
    if (won.status === "fulfilled") setWinRate(won.value.data);
    if (dead.status === "fulfilled") setLost(dead.value.data);
    // Paused rows must stay visible, otherwise there is nothing left to resume.
    if (enr.status === "fulfilled") {
      setEnrollments(enr.value.data.enrollments.filter(
        (item) => item.status === "active" || item.status === "paused"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminApi.get("/crm/sequences/channel-plan", { params: { capacity } })
      .then(({ data }) => setPlan(data)).catch(() => {});
  }, [capacity]);

  const act = async (fn, message) => {
    try {
      await fn();
      if (message) toast.success(message);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const enroll = async (event) => {
    event.preventDefault();
    if (!enrollForm.sequence_id || !enrollForm.list_id) {
      toast.error("Pick a cadence and a target list");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/crm/sequences/${enrollForm.sequence_id}/enroll`, {
        list_id: enrollForm.list_id, limit: Number(enrollForm.limit),
      });
      toast.success(`${data.enrolled} account(s) enrolled${data.skipped.length
        ? `, ${data.skipped.length} skipped` : ""}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-7" data-testid="admin-sequences-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Sequences</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Work the list, step by step
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
          A cadence holds the touches. Enrol a target list and each account gets one step at a time,
          written out with its own filing facts. Mail goes out of your own inbox — one click opens
          the draft, one click logs it and schedules the next touch.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[["Due now", queue.due_today, "queue-due-stat"],
          ["Scheduled ahead", queue.upcoming, "queue-upcoming-stat"],
          ["Touches logged", queue.sent_total, "queue-sent-stat"]].map(([label, value, testid]) => (
          <div key={label} className="rounded-xl border border-cream/10 bg-[#10211A] px-5 py-5"
               data-testid={testid}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/45">{label}</p>
            <p className="mt-3 font-serif text-3xl leading-none text-cream">{value}</p>
          </div>
        ))}
      </div>

      <Panel title="Today's queue" testid="panel-sequence-queue"
             action={<span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
               {queue.steps.length} ready
             </span>}>
        <ul className="divide-y divide-cream/10">
          {queue.steps.map((step) => (
            <StepCard key={step.id} step={step}
                      onSent={(s) => act(() => adminApi.post(`/crm/sequences/steps/${s.id}/sent`, {}),
                                         "Logged — next touch scheduled")}
                      onSkip={(s) => act(() => adminApi.post(`/crm/sequences/steps/${s.id}/skip`, {}),
                                         "Step skipped")}
                      onMeeting={(s) => act(async () => {
                        const { data } = await adminApi.post(
                          `/crm/sequences/steps/${s.id}/meeting`, {});
                        toast.success(data.stage_moved
                          ? `${s.account_name} moved to meeting set — cadence stopped`
                          : `Meeting logged on ${s.account_name} — cadence stopped`);
                      })}
                      onFindEmail={async (s) => {
                        try {
                          const { data } = await adminApi.post(
                            `/crm/accounts/${s.account_id}/find-email`);
                          if (data.matched && data.email) {
                            toast.success(`Found ${data.email}`);
                          } else {
                            toast.error(data.note || "No email found for this contact");
                          }
                          load();
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }} />
          ))}
          {queue.steps.length === 0 ? (
            <li className="py-4 text-sm text-cream/40" data-testid="queue-empty">
              Nothing due. Enrol a target list below and the first touch appears here.
            </li>
          ) : null}
        </ul>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Cadences" testid="panel-sequence-list"
               action={
                 <button type="button" data-testid="sequence-starter-button"
                         onClick={() => act(() => adminApi.post("/crm/sequences/starter"),
                                            "Starter cadence added")}
                         className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/45 transition-colors duration-200 hover:text-gold">
                   Add starter cadence
                 </button>
               }>
          <ul className="divide-y divide-cream/10">
            {sequences.map((sequence) => (
              <li key={sequence.id} className="py-3" data-testid={`sequence-${sequence.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-cream/90">{sequence.name}</p>
                  <button type="button" data-testid={`sequence-delete-${sequence.id}`}
                          onClick={() => act(() => adminApi.delete(`/crm/sequences/${sequence.id}`),
                                             "Cadence removed")}
                          className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/25 transition-colors duration-200 hover:text-terra">
                    Delete
                  </button>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-cream/45">{sequence.description}</p>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                  {sequence.steps.length} steps · {sequence.active_enrollments} active ·{" "}
                  {sequence.completed_enrollments} finished
                </p>
              </li>
            ))}
            {sequences.length === 0 ? (
              <li className="py-3 text-sm text-cream/40" data-testid="sequences-empty">
                No cadences yet — add the starter and edit it from there.
              </li>
            ) : null}
          </ul>
        </Panel>

        <Panel title="Enrol a target list" testid="panel-sequence-enroll">
          <form onSubmit={enroll} className="space-y-3" data-testid="sequence-enroll-form">
            <select value={enrollForm.sequence_id} className={inputClass}
                    data-testid="enroll-sequence-select"
                    onChange={(e) => setEnrollForm({ ...enrollForm, sequence_id: e.target.value })}>
              <option value="">Choose a cadence</option>
              {sequences.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={enrollForm.list_id} className={inputClass} data-testid="enroll-list-select"
                    onChange={(e) => setEnrollForm({ ...enrollForm, list_id: e.target.value })}>
              <option value="">Choose a target list</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>{l.name} ({l.count})</option>
              ))}
            </select>
            <input type="number" min="1" max="500" value={enrollForm.limit} className={inputClass}
                   data-testid="enroll-limit-input"
                   onChange={(e) => setEnrollForm({ ...enrollForm, limit: e.target.value })} />
            <button type="submit" disabled={busy} className={buttonClass} data-testid="enroll-submit">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Enrol top accounts
            </button>
            <p className="text-xs leading-relaxed text-cream/35">
              Highest-fit accounts first. Anything marked do-not-contact, or already in a cadence,
              is skipped and reported back.
            </p>
          </form>
        </Panel>
      </div>

      <Panel title="Build a cadence" testid="panel-sequence-builder">
        <Builder onSaved={load} />
      </Panel>

      <Panel title="Needs a new angle" testid="panel-sequence-nudges"
             action={<span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
               {nudges.length} flagged
             </span>}>
        <ul className="divide-y divide-cream/10">
          {nudges.map((nudge) => (
            <li key={nudge.id} className="flex flex-wrap items-center justify-between gap-3 py-3"
                data-testid={`nudge-${nudge.id}`}>
              <div className="min-w-0">
                <Link to={`/admin/accounts/${nudge.account_id}`}
                      className="text-sm text-cream transition-colors duration-200 hover:text-gold">
                  {nudge.account_name || nudge.title}
                </Link>
                <p className="mt-0.5 text-xs leading-relaxed text-cream/45">{nudge.notes}</p>
              </div>
              <button type="button" data-testid={`nudge-dismiss-${nudge.id}`}
                      onClick={() => act(() => adminApi.post(
                        `/crm/sequences/nudges/${nudge.id}/dismiss`), "Cleared")}
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30 transition-colors duration-200 hover:text-gold">
                Clear
              </button>
            </li>
          ))}
          {nudges.length === 0 ? (
            <li className="py-3 text-sm text-cream/40" data-testid="nudges-empty">
              Nothing stalled. Anything that takes three touches without a word back lands here.
            </li>
          ) : null}
        </ul>
      </Panel>

      <Panel title="Cadence scoreboard" testid="panel-sequence-scoreboard">
        <Scoreboard data={scores} />
      </Panel>

      <Panel title="Signed business by cadence" testid="panel-sequence-winrate">
        <WinRate data={winRate} />
      </Panel>

      <Panel title="This week's channel budget" testid="panel-channel-plan">
        <ChannelBudget data={plan} capacity={capacity} onCapacity={setCapacity} />
      </Panel>

      <Panel title="Why deals died" testid="panel-sequence-lostreasons">
        <LostReasons data={lost} />
      </Panel>

      <Panel title="In flight" testid="panel-sequence-enrollments">
        <ul className="divide-y divide-cream/10">
          {enrollments.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-3"
                data-testid={`enrollment-${item.id}`}>
              <div className="min-w-0">
                <Link to={`/admin/accounts/${item.account_id}`}
                      className="text-sm text-cream transition-colors duration-200 hover:text-gold">
                  {item.account_name}
                </Link>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                  {item.sequence_name} · next {item.next_due || "—"}
                  {item.status === "paused" ? " · paused" : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {item.status === "active" ? (
                  <button type="button" data-testid={`enrollment-pause-${item.id}`}
                          onClick={() => act(() => adminApi.patch(
                            `/crm/sequences/enrollments/${item.id}`, { status: "paused" }), "Paused")}
                          className="text-cream/40 transition-colors duration-200 hover:text-gold">
                    <PauseCircle className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="button" data-testid={`enrollment-resume-${item.id}`}
                          onClick={() => act(() => adminApi.patch(
                            `/crm/sequences/enrollments/${item.id}`, { status: "active" }), "Resumed")}
                          className="text-cream/40 transition-colors duration-200 hover:text-sage">
                    <PlayCircle className="h-4 w-4" />
                  </button>
                )}
                <button type="button" data-testid={`enrollment-stop-${item.id}`}
                        onClick={() => act(() => adminApi.patch(
                          `/crm/sequences/enrollments/${item.id}`, { status: "stopped" }), "Stopped")}
                        className="text-cream/40 transition-colors duration-200 hover:text-terra">
                  <Square className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
          {enrollments.length === 0 ? (
            <li className="py-3 text-sm text-cream/40" data-testid="enrollments-empty">
              No accounts are being worked yet.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}
